import React, { useEffect, useMemo, useState } from 'react'
import { Box, Center, Loader, Stack, Group, Alert, Paper } from '@mantine/core'
import { OneLevelCommentList, CommentInputRow } from '@gunslop/ui'
import type { PlatformCommentNode } from '@gunslop/ui'
import { FiArrowLeft, FiHeart, FiMessageSquare, FiSend, FiShare2, FiTrash2 } from 'react-icons/fi'
import { useHistory, useParams } from 'react-router-dom'
import agent from '../../app/api/agent'
import { toAbsoluteMediaUrl } from '../../app/api/resolveApiBaseUrl'
import { store } from '../../app/stores/store'
import { FlagTargetType } from '../../app/models/flag'
import { ReportContentModal, ConfirmModal, AppButton, AppIconButton, AppText } from '../../app/common/ui'
import { formatSlopDate } from './slopFeedConstants'
import {
  SLOP_FEED_MOBILE_MQ,
  slopPostPageShellSx,
  slopDetailNativeVideoSx,
  slopDetailVideoBleedSx,
  slopDetailEmbedSx,
  FEED_ACTION_BAR_SX,
  slopThumbActionIconSx
} from './slopFeedLayout'
import type { SlopPostDto, SlopPostCommentDto } from '../../app/models/slopPost'

export default function SlopPostPage () {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const currentUser = store.userStore.user
  const isLoggedIn = !!currentUser
  const isAdmin = currentUser?.isAdmin ?? false

  const [post, setPost] = useState<SlopPostDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [comments, setComments] = useState<SlopPostCommentDto[]>([])
  const [commentsLoading, setCommentsLoading] = useState(false)
  const [newComment, setNewComment] = useState('')
  const [commentSubmitting, setCommentSubmitting] = useState(false)

  const [shareCopied, setShareCopied] = useState(false)
  const [reportOpen, setReportOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const canManage = useMemo(() => {
    if (!post || !currentUser) return false
    return post.userId === currentUser.id || isAdmin
  }, [currentUser, isAdmin, post])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    void agent.SlopFeed.getDetails(id)
      .then((p) => { if (!cancelled) setPost(p) })
      .catch(() => { if (!cancelled) { setPost(null); setError('This post is not available.') } })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [id])

  useEffect(() => {
    if (!post) return
    let cancelled = false
    setCommentsLoading(true)
    void agent.SlopFeed.getComments(post.id)
      .then((c) => { if (!cancelled) setComments(c) })
      .catch(() => { if (!cancelled) setComments([]) })
      .finally(() => { if (!cancelled) setCommentsLoading(false) })
    return () => { cancelled = true }
  }, [post?.id])

  const requireAuth = (action: () => void) => {
    if (!isLoggedIn) { history.push('/register'); return }
    action()
  }

  const toggleLike = async () => {
    if (!post) return
    const res = await agent.SlopFeed.toggleLike(post.id)
    setPost({ ...post, likeCount: res.newLikeCount, isLikedByCurrentUser: res.isLiked })
  }

  const share = async () => {
    if (!post) return
    const url = `${window.location.origin}/slop-feed/${post.id}`
    if (navigator.share) {
      try { await navigator.share({ title: post.title || 'GunSlop Video', url }) } catch { /* cancel */ }
    } else {
      await navigator.clipboard.writeText(url)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    }
  }

  const submitComment = async () => {
    if (!post) return
    const body = newComment.trim()
    if (!body) return
    setCommentSubmitting(true)
    try {
      const created = await agent.SlopFeed.addComment(post.id, body)
      setComments((prev) => [...prev, created])
      setNewComment('')
      setPost((p) => p ? { ...p, commentCount: p.commentCount + 1 } : p)
    } finally {
      setCommentSubmitting(false)
    }
  }

  const renderCommentActions = (_c: PlatformCommentNode, _depth: number) => {
    if (!post) return null
    return (
      <Group spacing={6} mt={6}>
        <AppButton
          variant="subtle"
          size="xs"
          onClick={() => requireAuth(() => setReportOpen(true))}
        >
          Report
        </AppButton>
      </Group>
    )
  }

  if (loading) {
    return (
      <Center style={{ minHeight: '50vh' }}>
        <Loader />
      </Center>
    )
  }

  if (!post) {
    return (
      <Box sx={slopPostPageShellSx}>
        <AppButton variant="subtle" leftIcon={<FiArrowLeft size={14} />} onClick={() => history.push('/slop-feed')}>
          Back
        </AppButton>
        <Alert color="yellow" mt="md">{error ?? 'This post is not available.'}</Alert>
      </Box>
    )
  }

  return (
    <Box sx={slopPostPageShellSx}>
      <AppButton
        variant="subtle"
        leftIcon={<FiArrowLeft size={14} />}
        onClick={() => history.push('/slop-feed')}
        sx={{ color: 'var(--gs-muted)', marginBottom: 8, paddingLeft: 0 }}
      >
        Slop feed
      </AppButton>

      <Stack gap="md">
        {post.isEmbedded ? (
          <Box sx={slopDetailEmbedSx}>
            <iframe
              title={post.title ?? 'Embedded video'}
              src={post.embedSourceUrl ?? ''}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
              allowFullScreen
            />
          </Box>
        ) : (
          <Box
            component="video"
            controls
            playsInline
            sx={{ ...slopDetailNativeVideoSx, ...slopDetailVideoBleedSx }}
          >
            <source src={toAbsoluteMediaUrl(post.videoUrl)} />
          </Box>
        )}

        <Stack
          gap={6}
          sx={{
            [SLOP_FEED_MOBILE_MQ]: { paddingLeft: 12, paddingRight: 12 }
          }}
        >
          <AppText sx={{ fontSize: 18, fontWeight: 700 }}>{post.title}</AppText>
          {post.description && <AppText sx={{ color: 'var(--gs-muted)' }}>{post.description}</AppText>}
          <AppText sx={{ fontSize: 12, color: 'var(--gs-muted)' }}>
            by {post.username} · {formatSlopDate(post.createdAt)}
          </AppText>

          {(post.firearmModelId || post.cartridgeSpecId) && (
            <Paper withBorder p={10} sx={{ borderColor: 'var(--gs-border)', background: 'var(--gs-surface1)' }}>
              <AppText sx={{ fontSize: 12, color: 'var(--gs-muted)' }}>{post.linkedCaption}</AppText>
            </Paper>
          )}
        </Stack>

        <Group spacing="md" sx={FEED_ACTION_BAR_SX} align="center" wrap="nowrap">
          <AppIconButton
            variant="subtle"
            size="lg"
            sx={slopThumbActionIconSx}
            onClick={() => requireAuth(() => void toggleLike())}
            aria-label={post.isLikedByCurrentUser ? 'Unlike' : 'Like'}
          >
            <FiHeart size={22} />
          </AppIconButton>
          <AppIconButton
            variant="subtle"
            size="lg"
            sx={slopThumbActionIconSx}
            onClick={() => {
              const el = document.getElementById('slop-comments')
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
            aria-label="Comments"
          >
            <FiMessageSquare size={22} />
          </AppIconButton>
          <AppIconButton
            variant="subtle"
            size="lg"
            sx={{ marginLeft: 'auto', ...slopThumbActionIconSx }}
            onClick={() => void share()}
            aria-label="Share"
          >
            <FiShare2 size={22} />
          </AppIconButton>
          {canManage && (
            <AppIconButton
              variant="subtle"
              size="lg"
              sx={slopThumbActionIconSx}
              onClick={() => setDeleteOpen(true)}
              aria-label="Delete"
            >
              <FiTrash2 size={22} />
            </AppIconButton>
          )}
        </Group>

        {shareCopied && <Alert color="green">Copied link.</Alert>}

        <Box
          id="slop-comments"
          sx={{ [SLOP_FEED_MOBILE_MQ]: { paddingLeft: 12, paddingRight: 12 } }}
        >
          <AppText sx={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            {post.commentCount} Comment{post.commentCount === 1 ? '' : 's'}
          </AppText>

          {isLoggedIn ? (
            <CommentInputRow
              input={{
                value: newComment,
                onChange: (e) => setNewComment(e.currentTarget.value),
                placeholder: 'Add a comment…',
                disabled: commentSubmitting,
                multiline: true,
                minRows: 1,
                maxRows: 4
              }}
              action={(
                <AppButton
                  variant="light"
                  size="sm"
                  leftIcon={<FiSend size={14} />}
                  loading={commentSubmitting}
                  disabled={!newComment.trim()}
                  onClick={() => void submitComment()}
                >
                  Post
                </AppButton>
              )}
            />
          ) : (
            <Alert color="dark">
              <AppText>Sign up or log in to comment.</AppText>
            </Alert>
          )}

          <Box mt="md">
            <OneLevelCommentList
              comments={comments}
              loading={commentsLoading}
              formatCreatedAt={formatSlopDate}
              renderActions={renderCommentActions}
            />
          </Box>
        </Box>
      </Stack>

      <ReportContentModal
        opened={reportOpen}
        onClose={() => setReportOpen(false)}
        targetType={FlagTargetType.SlopPost}
        targetId={post.id}
        contentLabel="slop post"
      />

      <ConfirmModal
        opened={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Delete post?"
        message="Delete this slop post? This cannot be undone."
        variant="danger"
        onConfirm={() => {
          void (async () => {
            await agent.SlopFeed.deletePost(post.id)
            history.push('/slop-feed')
          })()
        }}
      />
    </Box>
  )
}

